import { Badge } from "@/components/ui/badge";
import { PencilIcon } from "lucide-react";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { useContext, useMemo } from "react";
import { useLocation } from "@/providers/LocationContextProvider";
import { locationRoute } from "@/App";
import { useTables } from "@/hooks/useTables";
import { useLocationMenus } from "@/hooks/useLocationMenus";
import { useModeSelect } from "@/hooks/useModeSelect";

function ModeIndicator(props?: { disabled?: boolean }) {
  const { locationId } = locationRoute.useParams();
  const { tableId, orderType } = useLocation();
  const { hasDineInMenu, hasTakeAwayMenu } = useLocationMenus();
  const { data: tables } = useTables(locationId, true);
  const { setShowSelector } = useModeSelect();

  const canChangeOrderType = useMemo(() => {
    return hasDineInMenu && hasTakeAwayMenu;
  }, [hasDineInMenu, hasTakeAwayMenu]);

  const selectedTable = useMemo(() => {
    return tables?.find((table) => table.id === tableId);
  }, [tables, tableId]);

  const { showTableSelector: showIndicator, lockTableSelector } = useContext(
    LocationConfigurationContext
  );

  const label = useMemo(() => {
    if (!orderType) return "";

    if (orderType === "TAKE_AWAY") {
      return "Take-away";
    }
    if (orderType === "DELIVERY") {
      return "Delivery";
    }
    if (orderType === "DINE_IN" && !selectedTable) {
      return "Select Table";
    }

    if (!selectedTable) {
      return "";
    }

    return `Location: ${selectedTable?.tableIdentifier ?? "Select Table"}`;
  }, [orderType, selectedTable]);

  const disabled = useMemo(() => {
    if (orderType !== "DINE_IN" && canChangeOrderType === false) return true;
    return lockTableSelector === true || props?.disabled === true;
  }, [lockTableSelector, props?.disabled, canChangeOrderType, orderType]);

  return (
    showIndicator && (
      <div className="flex items-center gap-4">
        {label && (
          <button
            aria-label="Change table"
            onClick={() => {
              if (disabled) return;
              setShowSelector(true);
            }}
            disabled={disabled}
          >
            <Badge className="hover:bg-primary transition-colors flex items-center gap-x-2">
              <span className="whitespace-nowrap font-medium tracking-tight">
                {label}
              </span>
              {disabled === false && <PencilIcon size={"12"} />}
            </Badge>
          </button>
        )}
      </div>
    )
  );
}

export { ModeIndicator };
